import React, { useCallback, useRef, useState } from 'react';
import { Section } from '../components/section';
import { sections } from '../hooks/use-section-analytics';
import { images } from '../constants/images';
import Sprite from '../sprites/sprite';
import { AmplitudeEvent, ButtonTag, analyticsState } from '../state/amplitude';
import { FormattedString, useMessages } from '../i18n/useMessages';

const firstColumn = [images['gallery-2a'], images['welcome'], images['yoga']];
const secondColumn = [
  // images['ambience'],
  images['meditation'],
  images['gallery-5-v2'],
];
const thirdColumn = [images['campsite'], images['gallery-6-v2'], images['welcome2']];
const columns = [firstColumn, secondColumn, thirdColumn];

const TESTIMONIAL_VIDEO_URL =
  'https://pub-3acbfdb36a6a4a758770a8294a62150d.r2.dev/camp67%20Testimonials%20Compressed.mp4';

export const WeLoveKumbhSection = () => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const onClickPlay = useCallback(() => {
    setPlaying(true);
    analyticsState.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      buttonSource: ButtonTag.PLAY_TESTIMONIAL_MONTAGE,
    });
    // Start playing the video
    setTimeout(() => {
      videoRef.current?.play();
    }, 100);
  }, []);

  const getMessage = useMessages();
  const decadeOldStory = getMessage('camp67-we-love-kumbh.decade-old');
  const rtoHosts = getMessage('camp67-we-love-kumbh.rto-kumbh-hosts');
  const seeMoreOn = getMessage('camp67-link.see-more-on');

  return (
    <>
      <Section
        className="py-16 md:py-20 lg:py-24 relative"
        id={sections['galleryWelcome']}
      >
        {/* Subtle background treatment */}
        <div className="absolute inset-0 bg-gradient-to-b from-green-50/30 via-white to-white" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          {/* Welcome arrow with better positioning */}
          <Sprite
            name="welcome-arrow"
            width={110}
            height={135}
            className="hidden lg:block w-24 h-28 absolute -left-8 -top-16 text-green-200 opacity-60"
          />

          {/* Header Section */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-block mb-3">
              <span className="text-green-800 font-bold text-xs uppercase tracking-widest bg-green-100 px-3 py-1.5 rounded-sm">
                {decadeOldStory}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-serif font-semibold text-gray-900 mb-6 leading-tight">
              <FormattedString
                message="camp67-we-love-kumbh.full-heading"
                components={{
                  'love-highlight': (
                    <span className="text-green-600 relative italic">
                      <u className="decoration-green-300 decoration-4 underline-offset-8">
                        love
                      </u>
                    </span>
                  ),
                }}
              />
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto font-medium">
              {rtoHosts}
            </p>
          </div>

          {/* Video Section - Memories from 2025 */}
          <div className="max-w-4xl mx-auto mb-16">
            <h3 className="text-center text-2xl lg:text-3xl font-serif font-medium text-gray-800 mb-8">
              Memories from Magh Mela 2026
            </h3>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
              {playing ? (
                <video
                  ref={videoRef}
                  className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
                  src={TESTIMONIAL_VIDEO_URL}
                  controls
                  controlsList="nodownload"
                  autoPlay
                  playsInline
                />
              ) : (
                <div
                  className="relative w-full h-[300px] md:h-[400px] lg:h-[500px] group cursor-pointer"
                  onClick={onClickPlay}
                >
                  <img
                    className="w-full h-full object-cover"
                    src={images['welcome2']}
                    alt="Testimonial montage thumbnail"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/20 w-24 h-24 backdrop-blur-sm p-6 rounded-full group-hover:bg-white/30 transition-all duration-300 group-hover:scale-110 flex items-center justify-center">
                      <Sprite
                        name="play"
                        width="64"
                        height="64"
                        className="text-white drop-shadow-lg w-12 h-12"
                      />
                    </div>
                  </div>

                  {/* Video description */}
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <h4 className="font-serif font-bold text-xl lg:text-2xl mb-2 drop-shadow-lg">
                      Testimonial Montage
                    </h4>
                    <p className="text-sm lg:text-base text-white/90 drop-shadow-md">
                      Hear from our visitors
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Photo Gallery with improved spacing */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {columns.map((column, index) => (
                <div key={index} className="space-y-4 lg:space-y-6">
                  {column.map((src, key) => (
                    <div
                      key={key}
                      className="group overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <img
                        src={src}
                        className="w-full object-cover select-none pointer-events-none group-hover:scale-105 transition-transform duration-500"
                        alt=""
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Footer link with better styling */}
          <div className="text-center">
            <p className="text-gray-600 text-sm lg:text-base">
              {seeMoreOn}{' '}
              <a
                className="text-blue-600 hover:text-blue-700 underline decoration-2 underline-offset-4 font-medium transition-colors"
                href="https://kumbhsukrit.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                kumbhsukrit.com →
              </a>
            </p>
          </div>
        </div>
      </Section>
    </>
  );
};
